import React from 'react';

import { PALETTE } from '../constants';

import { compact } from './dashboardFormatting';

// IB-677 rework — Overview diverging chart: budget risks (red, left) vs budget
// opportunities (green, right) weighted value per department, each split in-AFC
// (solid) / not-in-AFC (tint), with totals at the bar ends. Built as flex-div bars
// (Procurement idiom) rather than an Apex diverging bar — the old Apex version
// rendered one-sided with a reversed axis when a side was empty.
// data: { deptName: { risk_afc, risk_non_afc, opp_afc, opp_non_afc } }

function Swatch({ colour, label }) {
    return (
        <span className="tw-inline-flex tw-items-center tw-gap-1.5">
            <i className="tw-inline-block tw-h-2 tw-w-2 tw-rounded-sm" style={{ background: colour }} />
            {label}
        </span>
    );
}

export default function DepartmentAfcChart({ data }) {
    const rows = Object.entries(data || {})
        .map(([dept, value]) => ({
            dept,
            riskIn: Number(value.risk_afc) || 0,
            riskOut: Number(value.risk_non_afc) || 0,
            oppIn: Number(value.opp_afc) || 0,
            oppOut: Number(value.opp_non_afc) || 0,
        }))
        .filter((row) => row.riskIn + row.riskOut + row.oppIn + row.oppOut > 0)
        .sort((a, b) => b.riskIn + b.riskOut - (a.riskIn + a.riskOut));

    if (!rows.length) {
        return <div className="tw-text-sm tw-text-charcoal-50">{$t('No data')}</div>;
    }

    // One shared scale across both sides so widths are comparable left-to-right.
    const max = Math.max(1, ...rows.map((row) => Math.max(row.riskIn + row.riskOut, row.oppIn + row.oppOut)));
    const width = (value) => `${(value / max) * 100}%`;

    const totals = rows.reduce(
        (carry, row) => ({
            riskIn: carry.riskIn + row.riskIn,
            riskOut: carry.riskOut + row.riskOut,
            oppIn: carry.oppIn + row.oppIn,
            oppOut: carry.oppOut + row.oppOut,
        }),
        { riskIn: 0, riskOut: 0, oppIn: 0, oppOut: 0 },
    );

    const columns = '1fr 120px 1fr';

    return (
        <div>
            <div
                className="tw-mb-2 tw-grid tw-gap-2 tw-text-2xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-charcoal-50"
                style={{ gridTemplateColumns: columns }}
            >
                <div className="tw-text-end">◀ {$t('Budget risks (weighted)')}</div>
                <div />
                <div>{$t('Budget opportunities (weighted)')} ▶</div>
            </div>

            {rows.map((row) => (
                <div key={row.dept} className="tw-mb-1.5 tw-grid tw-items-center tw-gap-2 tw-text-xs" style={{ gridTemplateColumns: columns }}>
                    <div className="tw-flex tw-items-center tw-justify-end tw-gap-1.5">
                        <span className="tw-whitespace-nowrap tw-text-2xs tw-font-semibold tw-text-charcoal-75">{compact(row.riskIn + row.riskOut)}</span>
                        <span className="tw-h-3.5 tw-rounded-l" style={{ width: width(row.riskOut), background: PALETTE.red25 }} />
                        <span className="tw-h-3.5" style={{ width: width(row.riskIn), background: PALETTE.red }} />
                    </div>
                    <div className="tw-truncate tw-text-center tw-font-medium tw-text-charcoal-75">{row.dept}</div>
                    <div className="tw-flex tw-items-center tw-gap-1.5">
                        <span className="tw-h-3.5" style={{ width: width(row.oppIn), background: PALETTE.green }} />
                        <span className="tw-h-3.5 tw-rounded-r" style={{ width: width(row.oppOut), background: PALETTE.green25 }} />
                        <span className="tw-whitespace-nowrap tw-text-2xs tw-font-semibold tw-text-charcoal-75">{compact(row.oppIn + row.oppOut)}</span>
                    </div>
                </div>
            ))}

            <div className="tw-mt-3 tw-text-2xs tw-leading-snug tw-text-charcoal-50">
                <div>
                    {$t('Solid = in AFC · tint = not in AFC · totals at bar ends.')} {$t('Risks')}: {$t('in AFC')}{' '}
                    <b className="tw-font-semibold tw-text-charcoal-75">{compact(totals.riskIn)}</b> · {$t('not in AFC')}{' '}
                    <b className="tw-font-semibold tw-text-charcoal-75">{compact(totals.riskOut)}</b>. {$t('Opportunities')}: {$t('in AFC')}{' '}
                    <b className="tw-font-semibold tw-text-charcoal-75">{compact(totals.oppIn)}</b> · {$t('not in AFC')}{' '}
                    <b className="tw-font-semibold tw-text-charcoal-75">{compact(totals.oppOut)}</b>.
                </div>
                <div className="tw-mt-1.5 tw-flex tw-flex-wrap tw-gap-x-3 tw-gap-y-1">
                    <Swatch colour={PALETTE.red} label={$t('risk in AFC')} />
                    <Swatch colour={PALETTE.red25} label={$t('risk not in AFC')} />
                    <Swatch colour={PALETTE.green} label={$t('opp in AFC')} />
                    <Swatch colour={PALETTE.green25} label={$t('opp not in AFC')} />
                </div>
            </div>
        </div>
    );
}
